import { API_MANAGE_LISTS } from '../../constants/endpoints'
import { APPSTATE } from '../../state'
import { controller } from '../../utilities/abortController'
import type { EdgeApiResponse } from './utilities'

export const enum ManageListsType {
    friends = 'friends',
    bookmarks = 'bookmarks',
    ignores = 'ignores',
    hidden = 'hidden',
}

export const enum ListOrderKeys {
    username = 'Username (A-Z)',
    username_desc = 'Username (Z-A)',
    logged_in_recently = 'Logged in Most Recently',
    logged_in_least_recently = 'Logged in Least Recently',
    added_most_recently = 'Added Most Recently',
    added_least_recently = 'Added Least Recently',
}

// TODO: add these for ignored and hidden models
export const enum ListOrderTypes {
    alphabetical = 'alphabetical',
    friends_added_most_recently = 'tm_added',
    bookmarks_added_most_recently = 'bookmark_date',
    last_login_range = 'last_login_range',
}

export const ListOrderDictionary: {
    [option: string]: Partial<ListRequestParams>
} = {
    [ListOrderKeys.username]: { order: ListOrderTypes.alphabetical, desc: 0 },
    [ListOrderKeys.username_desc]: {
        order: ListOrderTypes.alphabetical,
        desc: 1,
    },
    [ListOrderKeys.logged_in_recently]: {
        order: ListOrderTypes.last_login_range,
        desc: 0,
    },
    [ListOrderKeys.logged_in_least_recently]: {
        order: ListOrderTypes.last_login_range,
        desc: 1,
    },
    [ListOrderKeys.added_most_recently]: {
        order: ListOrderTypes.friends_added_most_recently,
        desc: 1,
    },
    [ListOrderKeys.added_least_recently]: {
        order: ListOrderTypes.friends_added_most_recently,
        desc: 0,
    },
}

export const getRecentlyAddedKeyByListType = (listType: ManageListsType) => {
    // TODO: add these for ignored and hidden models
    switch (listType) {
        case ManageListsType.friends:
            return ListOrderTypes.friends_added_most_recently
        case ManageListsType.bookmarks:
            return ListOrderTypes.bookmarks_added_most_recently
    }
}

export type List =
    'friends_lists' | 'bookmarks' | 'ignore_lists' | 'hidden_models'

export const responseListTypeMap = {
    [ManageListsType.friends]: 'friends_lists',
    [ManageListsType.bookmarks]: 'bookmarks',
    [ManageListsType.ignores]: 'ignore_lists',
    [ManageListsType.hidden]: 'hidden_models',
} as const

const orderMap = {
    [ManageListsType.friends]: 'tm_added',
    [ManageListsType.bookmarks]: 'bookmark_date',
    [ManageListsType.ignores]: 'tm_added',
    [ManageListsType.hidden]: 'alphabetical',
} as const

type ResponseListType<T extends ManageListsType> = T extends 'friends'
    ? 'friends_lists'
    : T extends 'bookmarks'
      ? 'bookmarks'
      : T extends 'ignores'
        ? 'ignore_lists'
        : T extends 'hidden'
          ? 'hidden_models'
          : never

interface ManageListsApiResult {
    input: string
    user_id: number
    username: string
    result: number
}
type ManageListsApiResponse = EdgeApiResponse<Array<ManageListsApiResult>>

interface ManageListsApiOptions {
    /** remove from list (default = false, i.e. add to list) */
    remove?: boolean
}

/**
 * Add or remove users from a list (friends, bookmarks, ignores, hidden)
 * @param identifiers Array of usernames or user ids
 * @param options To remove from list, set options.remove = true
 * @returns Array of usernames that were successfully added/removed
 */
export async function manageListsApi(
    listType: ManageListsType,
    identifiers: Array<number | string>,
    options: ManageListsApiOptions = {},
) {
    const { remove = false } = options
    const method = remove ? 'DELETE' : 'PUT'
    const url = `${API_MANAGE_LISTS[listType]}/${identifiers.join(',')}`
    const { token = '' } = APPSTATE.session
    const response = await fetch(url, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ token }),
        signal: controller.signal,
    })
    const resp = (await response.json()) as ManageListsApiResponse
    if (!resp || typeof resp !== 'object' || resp.err || resp.errmsg) {
        throw new Error(`[manageListsApi] ${listType} Error: ${resp.errmsg}`)
    }
    const { result } = resp
    const usernames = result.map((r) => r.username)
    return usernames
}

export interface ListDataUser {
    /** YYYY-MM-DD HH:MM:SS */
    added: string
    color: string
    id: number
    /** YYYY-MM-DD */
    lastBroadcast?: string | null
    /** Number of days since last logged in */
    lastLoginRange: number
    level?: number | null
    name: string
}

export interface FriendsListRow {
    friend_id: number
    /** YYYY-MM-DD HH:MM:SS */
    tm_added: string
    /** YYYY-MM-DD */
    last_broadcast?: string | null
    /** same as friend_id */
    id?: number
    username?: string
    access_level?: 1 | 2 | 3 | 4 | 5
    /** color:#000000; font-family:Lucida Console; font-weight:bold; */
    font_style?: string
    chat_text_font?: number
    /** hex color, no # */
    chat_text_color?: string
    chat_opts?: number
    /** Number of days since last logged in */
    last_login_range?: number
}

export interface BookmarksListRow {
    bookmark_id: number
    /** YYYY-MM-DD HH:MM:SS */
    bookmark_date: string
    /** YYYY-MM-DD */
    last_broadcast?: string | null
    /** same as bookmark_id */
    id?: number
    username?: string
    access_level?: 4
    /** color:#000000; font-family:Lucida Console; font-weight:bold; */
    font_style?: string
    chat_text_font?: number
    /** hex color, no # */
    chat_text_color?: string
    chat_opts?: number
    /** Number of days since last logged in */
    last_login_range?: number
}

export interface IgnoreListRow {
    ignore_id: number
    /** YYYY-MM-DD HH:MM:SS */
    tm_added: string
    /** YYYY-MM-DD */
    last_broadcast?: string | null
    /** same as ignore_id */
    id?: number
    username?: string
    access_level?: 1 | 2 | 3 | 4
    /** color:#000000; font-family:Lucida Console; font-weight:bold; */
    font_style?: string
    chat_text_font?: number
    /** hex color, no # */
    chat_text_color?: string
    chat_opts?: number
    /** Number of days since last logged in */
    last_login_range?: number
}

export interface HiddenListRow {
    model_id: number
    /** YYYY-MM-DD */
    last_broadcast?: string | null
    /** same as model_id */
    id?: number
    username?: string
    access_level?: 4
    /** color:#000000; font-family:Lucida Console; font-weight:bold; */
    font_style?: string
    chat_text_font?: number
    /** hex color, no # */
    chat_text_color?: string
    chat_opts?: number
    /** Number of days since last logged in */
    last_login_range?: number
}

export type ManageListRow<T extends ManageListsType> = T extends 'friends'
    ? FriendsListRow
    : T extends 'bookmarks'
      ? BookmarksListRow
      : T extends 'ignores'
        ? IgnoreListRow
        : T extends 'hidden'
          ? HiddenListRow
          : never

export interface FriendsListResponse {
    data: Array<FriendsListRow>
    err: number
}

export interface BookmarksListResponse {
    data: Array<BookmarksListRow>
    err: number
}

export interface ManageListResponse<T extends ManageListsType> {
    data: Array<ManageListRow<T>>
    err: number
}

export interface PaginatedFriendsListResponse {
    data: {
        count: number
        limit: number
        list: 'friends_lists'
        offset: number
        rows: Array<FriendsListRow>
    }
    err: number
}

export interface PaginatedBookmarksListResponse {
    data: {
        count: number
        limit: number
        list: 'bookmarks'
        offset: number
        rows: Array<BookmarksListRow>
    }
    err: number
}

export interface PaginatedIgnoreListResponse {
    data: {
        count: number
        limit: number
        list: 'ignore_lists'
        offset: number
        rows: Array<IgnoreListRow>
    }
    err: number
}

export interface PaginatedHiddenModelsResponse {
    data: {
        count: number
        limit: number
        list: 'hidden_models'
        offset: number
        rows: Array<HiddenListRow>
    }
    err: number
}

export type PaginatedResponse =
    | PaginatedFriendsListResponse
    | PaginatedBookmarksListResponse
    | PaginatedIgnoreListResponse
    | PaginatedHiddenModelsResponse

export interface PaginatedListResponse<T extends ManageListsType> {
    data: {
        count: number
        limit: number
        list: ResponseListType<T>
        offset: number
        rows: Array<ManageListRow<T>>
    }
    err: number
}

export interface ListRequestParams {
    metadata?: 0 | 1
    limit?: number
    offset?: number
    user_details?: 'presentation'
    user_details_username_search?: string
    order?: ListOrderTypes
    desc?: 0 | 1
}

export const LIST_PAGE_SIZE = 250

export interface NormalizedListResponse {
    data?: {
        count: number
        limit: number
        list: List
        offset: number
        rows: Array<ListDataUser>
    }
    err?: number
}

const isFriendsListResponse = (
    json: PaginatedResponse,
): json is PaginatedFriendsListResponse => {
    return (json as PaginatedFriendsListResponse).data?.list === 'friends_lists'
}

const isBookmarksListResponse = (
    json: PaginatedResponse,
): json is PaginatedBookmarksListResponse => {
    return (json as PaginatedBookmarksListResponse).data?.list === 'bookmarks'
}

const isIgnoreListResponse = (
    json: PaginatedResponse,
): json is PaginatedIgnoreListResponse => {
    return (json as PaginatedIgnoreListResponse).data?.list === 'ignore_lists'
}

// const isHiddenListResponse = (
//     json: PaginatedResponse,
// ): json is PaginatedHiddenModelsResponse => {
//     return (json as PaginatedHiddenModelsResponse).data.list === 'hidden_models'
// }

function buildNormalized(json: PaginatedResponse): NormalizedListResponse {
    const normalizedRows = isFriendsListResponse(json)
        ? json.data.rows.map((row) => ({
              id: row.friend_id,
              added: row.tm_added,
              name: row.username,
              color: `#${row.chat_text_color}`,
              lastBroadcast: row.last_broadcast,
              level: row.access_level,
              lastLoginRange: row.last_login_range,
          }))
        : isBookmarksListResponse(json)
          ? json.data.rows.map((row) => ({
                id: row.bookmark_id,
                added: row.bookmark_date,
                name: row.username,
                color: `#${row.chat_text_color}`,
                lastBroadcast: row.last_broadcast,
                level: row.access_level,
                lastLoginRange: row.last_login_range,
            }))
          : isIgnoreListResponse(json)
            ? json.data.rows.map((row) => ({
                  id: row.id,
                  added: row.tm_added,
                  name: row.username,
                  color: `#${row.chat_text_color}`,
                  lastLoginRange: row.last_login_range,
              }))
            : json.data.rows.map((row) => ({
                  id: row.id,
                  added: '',
                  name: row.username,
                  color: `#${row.chat_text_color}`,
                  lastLoginRange: row.last_login_range,
              }))
    return {
        ...json,
        data: {
            ...json.data,
            rows: normalizedRows,
        },
    }
}

export async function fetchList(
    listType: ManageListsType,
    params: ListRequestParams,
): Promise<NormalizedListResponse> {
    const url = API_MANAGE_LISTS[listType]
    const {
        metadata = 1,
        limit = LIST_PAGE_SIZE,
        offset = 0,
        user_details = 'presentation',
        order,
        user_details_username_search = undefined,
    } = params
    const defaultOrder = orderMap[listType]
    const desc =
        params.desc ?? (listType === ManageListsType.hidden ? undefined : 1)
    const response = await fetch(
        `${url}?` +
            new URLSearchParams({
                metadata: metadata.toString(),
                limit: limit.toString(),
                offset: offset.toString(),
                order: order ?? defaultOrder,
                ...(user_details && { user_details }),
                ...(user_details_username_search && {
                    user_details_username_search,
                }),
                ...(desc !== undefined && { desc: desc.toString() }),
            }).toString(),
        { credentials: 'include', signal: controller.signal },
    )
    const json: PaginatedResponse = await response.json()
    const nextData = buildNormalized(json)
    return nextData
}

/**
 * Adapted from ManageLists.DateFormat
 * @param date YYYY-MM-DD HH:MM:SS
 */
export function mfcDateFormatter(date: string) {
    if (!date) return
    const months = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
    ]
    const ds = date.split(' ')
    const nextDs = ds[0].split('-')
    const mo = Number(nextDs[1]) - 1
    const month = months[mo]
    const day = nextDs[2]
    const year = nextDs[0]
    if (year === '0000') return 'before Jul 2016'
    const dat = month + ' ' + day + ' ' + year
    return dat
}

export const daysToString = (days: number) => {
    if (days === 0) return 'Today'
    if (days < 8) return 'In Last 7 Days'
    if (days < 15) return '2 Weeks Ago'
    if (days < 22) return '3 Weeks Ago'
    const months = Math.floor(days / 30)
    const monthsStr = months === 1 ? 'Month' : 'Months'
    if (days < 365) return `${months} ${monthsStr} Ago`
    const years = Math.floor(days / 365)
    const yearsStr = years === 1 ? 'Year' : 'Years'
    return `${years} ${yearsStr} Ago`
}
